// src/components/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import useAuth from '../hooks/useAuth';
import { User, Lock, AlertCircle } from 'lucide-react';
import PublicNavbar from './public/PublicNavbar';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated. Wait for user.role to be loaded first —
  // otherwise an undefined role slips into the "not user → terminal" branch and
  // mis-routes admins to /dashboardTerminal (a race that shows up on slower/mobile
  // connections where isAuthenticated flips true before /auth/me/ returns the role).
  useEffect(() => {
    if (isAuthenticated && user?.role) {
      if (['superadmin', 'admin'].includes(user.role)) {
        navigate('/admin-panel/dashboard');
      } else if (user.role === 'user') {
        navigate('/dashboard');
      } else {
        navigate('/dashboardTerminal');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!credentials.username || !credentials.password) {
      setError(t('pleaseEnterCredentials'));
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const result = await login(credentials.username, credentials.password);
      if (result.success) {
        // After successful login, we'll be redirected by the useEffect

      } else {
        setError(result.error || t('loginFailed'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex flex-col">
      {/* Include PublicNavbar at the top */}
      <PublicNavbar />
      
      {/* Login form */}
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-t-xl shadow-lg p-8 border-b-4 border-primary-600">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 text-white rounded-full mb-4 shadow-md">
                <span className="text-2xl font-bold">{t('companyNameShort')}</span>
              </div>
              <h1 className="text-2xl font-bold text-neutral-800">{t('login')}</h1>
            </div>
            
            {error && (
              <div className="mb-6 p-4 bg-danger-50 border-l-4 border-danger-500 text-danger-700 rounded flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">{t('username')}</label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-neutral-400" />
                  </div>
                  <input
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({
                      ...credentials,
                      username: e.target.value
                    })}
                    className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('username')}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">{t('password')}</label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-neutral-400" />
                  </div>
                  <input
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({
                      ...credentials,
                      password: e.target.value
                    })}
                    className="block w-full pl-10 pr-3 py-2.5 sm:text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('password')}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={credentials.rememberMe}
                    onChange={(e) => setCredentials({
                      ...credentials,
                      rememberMe: e.target.checked
                    })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-neutral-600">
                    {t('rememberMe')}
                  </label>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'}`}
              >
                {loading ? t('loading') : t('login')}
              </button>
            </form>
          </div>
          <div className="bg-neutral-50 p-4 text-center text-sm text-neutral-500 rounded-b-xl shadow-lg">
            &copy; {new Date().getFullYear()} TTS. {t('allRightsReserved')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;